import React, { useMemo } from 'react';
import { Button, Skeleton, Space, Typography } from 'antd';
import { CreateTopicModal } from 'components/modal/professor_area/forums/CreateTopicModal';
import { CrudFilters, HttpError, useModal } from '@refinedev/core';
import { EmptyTab } from '../EmptyTab';
import { ForumList } from './components/ForumList';
import { IForum } from 'interfaces/forum';
import { IProfessorAreaClassroom } from 'interfaces/professor_area_classrooms';
import { PlusOutlined } from '@ant-design/icons';
import { SearchFilters } from './components/SearchFilters';
import { useCanMultiple } from 'components/layout/sider';
import { useTable } from '@refinedev/antd';

export interface IFilter {
  topic: string;
  status: string;
}

export const TabForum: React.FC<{
  classroom: IProfessorAreaClassroom;
}> = ({ classroom }) => {
  const createTopicModalProps = useModal();
  const [createPermission, showPermission, deletePermission] = useCanMultiple([
    { resource: 'professor_area/forums', action: 'create' },
    { resource: 'professor_area/forums', action: 'show' },
    { resource: 'professor_area/forums', action: 'delete' }
  ]);
  const canCreateForum = createPermission.data?.can ?? false;
  const canShowForum = showPermission.data?.can ?? false;
  const canDeleteForum = deletePermission.data?.can ?? false;
  const {
    tableQueryResult: { data, isLoading, refetch },
    current,
    setCurrent,
    pageSize,
    searchFormProps
  } = useTable<IForum, HttpError, IFilter>({
    resource: 'professor_area/forums',
    initialPageSize: 10,
    onSearch: ({ topic, status }) => {
      const filters: CrudFilters = [];

      filters.push(
        {
          field: 'filter[by_title]',
          operator: 'eq',
          value: topic
        },
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: status
        }
      );
      return filters;
    }
  });

  const forums = data?.data as IForum[];

  const hasData = useMemo(() => {
    return !isLoading && forums && forums.length > 0;
  }, [isLoading, forums]);

  if (isLoading) {
    return (
      <Skeleton active paragraph={{ rows: 6 }} className="w-full h-full" />
    );
  }

  const EmptyComponent = () => {
    return (
      <EmptyTab
        title="Nenhum tópico encontrado"
        buttonText={canCreateForum ? 'Novo tópico' : undefined}
        onClick={
          canCreateForum ? () => createTopicModalProps.show() : undefined
        }
      />
    );
  };

  const ContentComponent = () => {
    return (
      <>
        <SearchFilters searchFormProps={searchFormProps} />
        <Space className="w-full justify-between items-center mb-4">
          <Typography.Title level={5} className="!m-0">
            Tópicos da turma
          </Typography.Title>
          {canCreateForum && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => createTopicModalProps.show()}
            >
              Novo tópico
            </Button>
          )}
        </Space>

        {forums?.length > 0 && (
          <ForumList
            data={forums}
            total={data!.total}
            pageSize={pageSize}
            currentPage={current}
            onPageChange={setCurrent}
            refetch={refetch}
            canShow={canShowForum}
            canDelete={canDeleteForum}
          />
        )}
      </>
    );
  };

  return (
    <>
      {hasData ? <ContentComponent /> : <EmptyComponent />}

      {canCreateForum && (
        <CreateTopicModal
          modalProps={createTopicModalProps}
          classroom={classroom}
        />
      )}
    </>
  );
};
